import { useEffect, useState } from "react";
import Slider from "react-slick";
import { useShows } from "@/context/shows";
import { useRouter } from "next/navigation";
import Container from "../Common/Container";
import { Skeleton } from "@mui/material";
import { useBuy } from "@/context/buy";
import BuyTicketModal from "../Modal/BuyTicketModal";
import { decryptInteger, encryptInteger } from "@/Utils/encryptInteger";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { formatMonthDayYear } from "@/Utils/dateFormat";

export default function ShowsSlider() {
  const { shows, loading, time } = useShows();
  const {
    openSingleModal,
    openModal,
    setOpenModal,
    selectedIndex,
    setSelectedIndex,
    setCustomModalData,
  } = useBuy();
  const [expanded, setExpanded] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isShowData, setIsShowData] = useState(true);
  const router = useRouter();

  const settings = {
    dots: false,
    arrows: true,
    infinite: shows?.length > 1,
    slidesToShow: 2,
    slidesToScroll: 1,
    nextArrow: <NextArrow />,
    beforeChange: (current, next) => setSelectedIndex(next),
    centerMode: false,
    centerPadding: "0px",
    initialSlide: 0,
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
          centerPadding: "0px",
          variableWidth: false,
        },
      },
    ],
  };

  useEffect(() => {
    if (!loading) {
      setOpenModal((prev) => ({
        ...prev,
        buy: false,
      }));
    }
  }, [loading]);

  useEffect(() => {
    if (!loading) {
      const timeout = setTimeout(() => setIsShowData(false), 200);
      return () => clearTimeout(timeout);
    } else {
      setIsShowData(true);
    }
  }, [loading]);

  useEffect(() => {
    let encryptedData = localStorage.getItem("user_data");
    if (encryptedData) {
      const decryptedData = JSON.parse(decryptInteger(encryptedData));
      setIsLoggedIn(decryptedData.user_type == 0);
    }
  }, []);

  const slugify = (str) =>
    str
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  return (
    <Container
      id="sliderComp"
      className=" -mt-[97px] min-h-[450px] lg:min-h-[626px] lg:mt-0 lg:py-[100px] lg:pr-[31px] lg:pl-[75px]"
    >
      <div
        style={{ backgroundImage: `url(${shows[selectedIndex]?.banner})` }}
        className="layout_bg_image hidden slideComp_bg lg:block"
      >
        <span className="bg_inner_top_layer"></span>
        <span className="bg_inner_shadow"></span>
      </div>

      {!isShowData && openModal.buy && (
        <AnimatePresence>
          <div className="absolute">
            <BuyTicketModal showData={shows[selectedIndex]} />
          </div>
        </AnimatePresence>
      )}
      {!isShowData && shows?.length === 0 ? (
        <div className="no-show-message relative z-[9] h-[450px] lg:h-[426px] flex flex-col items-center justify-center text-center">
          <svg
            width="50"
            height="50"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12 0C5.372 0 0 5.372 0 12C0 18.628 5.372 24 12 24C18.628 24 24 18.628 24 12C24 5.372 18.628 0 12 0ZM12 22C6.485 22 2 17.515 2 12C2 6.485 6.485 2 12 2C17.515 2 22 6.485 22 12C22 17.515 17.515 22 12 22ZM12 16.5C13.104 16.5 14 15.604 14 14.5C14 13.396 13.104 12.5 12 12.5C10.896 12.5 10 13.396 10 14.5C10 15.604 10.896 16.5 12 16.5ZM12 9C13.104 9 14 8.104 14 7C14 5.896 13.104 5 12 5C10.896 5 10 5.896 10 7C10 8.104 10.896 9 12 9Z"
              fill="#F44336"
            />
          </svg>
          <h3 className="text-xl mt-4">
            No Live Shows Available For This Week
          </h3>
          <p className="mt-2 text-sm">
            Check back soon for exciting new events!
          </p>
        </div>
      ) : (
        <div className="flex   lg:gap-[60px] items-center ">
          <div className="lg:w-[38%] relative z-20 hidden lg:block gradient-skeleton">
            <div className=" flex flex-col  lg:min-h-[428px]  h-full">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {isShowData ? (
                  <Skeleton variant="text" width={150} height={50} />
                ) : (
                  <p className=" text-[32px] leading-[48px] font-[700]  ">
                    {" "}
                    {time == "all" ? (
                      <span>Upcoming Shows</span>
                    ) : (
                      <span>
                        Live This <span className="capitalize">{time}</span>
                      </span>
                    )}{" "}
                  </p>
                )}
              </motion.div>
              <div className="flex-grow"></div>
              <div className="">
                <div className=" mt-[30px] ">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <p className=" font-[700] text-[48px] leading-[54px] line-clamp-2 overflow-hidden  ">
                      {isShowData ? (
                        <Skeleton variant="text" width={250} height={50} />
                      ) : (
                        shows[selectedIndex]?.name
                      )}
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="mt-2 flex gap-2 py-1"
                  >
                    <div className="flex gap-1 items-center">
                      {!isShowData && (
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M14.5098 10.7105C14.5098 9.3292 13.3905 8.20996 12.0093 8.20996C10.629 8.20996 9.50977 9.3292 9.50977 10.7105C9.50977 12.0907 10.629 13.21 12.0093 13.21C13.3905 13.21 14.5098 12.0907 14.5098 10.7105Z"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M11.9995 21C9.10148 21 4.5 15.9587 4.5 10.5986C4.5 6.40246 7.8571 3 11.9995 3C16.1419 3 19.5 6.40246 19.5 10.5986C19.5 15.9587 14.8985 21 11.9995 21Z"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                      <p className=" text-xl leading-[20px] font-[600]  ">
                        {isShowData ? (
                          <Skeleton variant="text" width={120} height={20} />
                        ) : (
                          shows[selectedIndex]?.venue?.name
                        )}
                      </p>
                    </div>
                    <p className=" text-xl leading-[22px]  font-[400] text-[#9E9E9E] ">
                      {isShowData ? (
                        <Skeleton variant="text" width={120} height={20} />
                      ) : (
                        shows[selectedIndex]?.venue?.city
                      )}
                      {!isShowData && `, ${shows[selectedIndex]?.venue?.state}`}
                    </p>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    {isShowData ? (
                      <Skeleton variant="text" width={150} height={30} />
                    ) : (
                      <p className="py-2 px-4 rounded-2xl bg-[#0000003D] my-2 text-lg font-[500] w-fit ">
                        {" "}
                        {shows[selectedIndex]?.restriction}
                      </p>
                    )}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    className="mt-2"
                  >
                    {isShowData ? (
                      <Skeleton variant="text" width={300} height={60} />
                    ) : (
                      <p className="text-[25px]  leading-[35px] font-[500]">
                        {(() => {
                          const fanDesc =
                            shows[selectedIndex]?.fan_description?.trim();
                          const desc =
                            (!isLoggedIn && fanDesc) ||
                              (isLoggedIn === true && fanDesc)
                              ? fanDesc
                              : shows[selectedIndex]?.Description;

                          if (!desc) return null;

                          const isLong = desc.length > 90;
                          const displayText = expanded
                            ? desc
                            : desc.slice(0, 88);

                          return (
                            <>
                              {displayText}
                              {isLong && (
                                <>
                                  ...
                                  <span
                                    className="text-[#FA2CB4] cursor-pointer"
                                    onClick={() => setExpanded(!expanded)}
                                  >
                                    {expanded ? " Read less" : " Read more"}
                                  </span>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </p>
                    )}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                    className="flex gap-3 mt-6 items-center"
                  >
                    {isShowData ? (
                      <Skeleton
                        variant="rectangular"
                        className="w-screen lg:w-[325px] py-6 rounded-md h-full"
                      />
                    ) : (
                      <>
                        {shows[selectedIndex]?.is_ticket_booked && (
                          <button
                            onClick={() =>
                              router.push(`/tickets/${shows[selectedIndex]?.booking_id}`)
                            }
                            className="view_ticket_btn w-full lg:w-2/4 font-bold lg:rounded-[10px]"
                          >
                            <p className="lg:text-[25px] min-w-max lg:font-[700] lg:leading-[22px] lg:py-4">
                              View Ticket
                            </p>
                          </button>
                        )}

                        <button
                          onClick={() => {
                            const selectedShow = shows[selectedIndex];
                            if (!selectedShow) return;

                            if (selectedShow.venue_ticket_web_link) {
                              openSingleModal("custom");
                              setCustomModalData({
                                showId: selectedShow.id,
                                link: selectedShow.venue_ticket_web_link,
                              });
                              return;
                            }

                            if (selectedShow.venue?.can_buy_tickets) {
                              openSingleModal("buy");
                            }
                          }}
                          className={`main_btn_bg font-bold min-w-max lg:rounded-[10px] w-full 
          ${shows[selectedIndex]?.is_ticket_booked ? "lg:w-2/4 px-8" : "lg:w-full"} 
          ${shows[selectedIndex]?.venue?.can_buy_tickets ? "" : "disableBuyTicket"}`}
                        >
                          <p className="lg:text-[25px] min-w-max lg:font-[700] lg:leading-[22px] lg:py-4">
                            Get Tickets
                          </p>
                        </button>
                      </>
                    )}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden w-full lg:w-[68%]">
            <div
              className={` pb-[1 2px] showsSlider ${isShowData && "single-slide-end"
                } slider-wrapper ${!isShowData && shows?.length === 1 ? "single-slide-end" : ""
                } `}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isShowData ? "isShowData" : "content"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Slider {...settings} className="relative">
                    {isShowData ? (
                      Array(2)
                        .fill(0)
                        .map((_, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            style={{ minWidth: "380px" }}
                            className="gradient-skeleton showsSliderSkeleton lg:pr-[28px] lg:w-[380px] lg:h-[380px] h-[450px] max-h-[450px]"
                          >
                            <Skeleton
                              variant="rectangular"
                              width="100%"
                              height="100%"
                              className="lg:rounded-2xl w-full h-full"
                            />
                          </motion.div>
                        ))
                    ) : shows?.length > 0 ? (
                      shows.map((show, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          onClick={() => {
                            const city = slugify(show?.venue?.city);
                            const state = slugify(show?.venue?.state);
                            const showNames = slugify(show?.name);
                            const venueName = slugify(show?.venue?.name);
                            const showDate = show?.show_date.split(" ")[0];

                            const fullPath = `/shows/${city}-${state}/${show.id}-for-${showNames}-at-${venueName}-on-${showDate}`;
                            router.push(fullPath);
                          }}
                          className="w-full gradient_border h-full cursor-pointer showsImage focus-visible:outline-0 relative"
                        >
                          <img
                            src={show?.banner}
                            className="lg:rounded-2xl h-[450px] lg:h-auto w-screen lg:w-[380px] lg:aspect-square object-cover"
                            alt={show?.name || "Show Banner"}
                          />
                          <div className="lg:hidden absolute bottom-0 w-full px-6 z-5 py-[17px] left-0 bg-gradient-to-t from-black via-black/70 to-transparent">
                            <div className="mt-4">
                              <p className="text-2xl leading-[32px] font-bold">
                                {show?.name}
                              </p>
                              <div className="mt-2 text-[14px] text-[#FFFFFF99]">
                                {show?.show_date
                                  ? formatMonthDayYear(show.show_date)
                                  : ""}{" "}
                                | {show?.venue?.city}, {show?.venue?.state}{" "}
                                {show?.restriction && (
                                  <span className="w-fit ml-2 text-[10px] leading-[10px] text-[#F9F9F9] px-[6px] rounded-2xl">
                                    {show?.restriction}
                                  </span>
                                )}
                              </div>
                              <p className="mt-2 text-[16px] leading-[22px]">
                                {(isLoggedIn &&
                                  show?.fan_description?.trim()) ||
                                  (!isLoggedIn && show?.fan_description?.trim())
                                  ? show.fan_description.slice(0, 90)
                                  : show?.Description?.slice(0, 90)}
                                ...
                              </p>
                            </div>
                            <div className="flex flex-col lg:flex-row gap-3 mt-4 w-full">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (show.venue_ticket_web_link) {
                                    openSingleModal("custom");
                                    setCustomModalData({
                                      showId: show.id,
                                      link: show.venue_ticket_web_link,
                                    });
                                    return;
                                  }

                                  if (show.venue?.can_buy_tickets) {
                                    openSingleModal("buy");
                                  }
                                }}
                                className={`
                                  ${show?.is_ticket_booked ? "lg:w-2/4" : "w-full"} 
                                  ${show?.venue?.can_buy_tickets ? "main_btn_bg" : "disableBuyTicket"} 
                                  rounded-[12px] font-bold
                                `}
                              >
                                <p className="py-3 text-lg font-extrabold leading-[22px]">
                                  Get Tickets
                                </p>
                              </button>
                              {show?.is_ticket_booked && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    router.push(`/tickets/${show?.booking_id}`);
                                  }}
                                  className="view_ticket_btn w-full lg:w-2/4 rounded-[12px] font-bold"
                                >
                                  <p className="py-3 text-lg font-extrabold leading-[22px]">View Ticket</p>
                                </button>
                              )}
                            </div>

                          </div>
                        </motion.div>
                      ))
                    ) : (
                      <p className="text-center w-full py-10 text-gray-400">
                        No shows available
                      </p>
                    )}
                  </Slider>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <div
      className="absolute right-0  cursor-pointer w-[161px] h-full flex items-center justify-end top-[0%]"
      onClick={onClick}
    >
      <div className="">
        <div className="slider_custom_next_arrow">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.42467 17.225C7.26634 17.225 7.10801 17.1667 6.98301 17.0417C6.74134 16.8 6.74134 16.4 6.98301 16.1583L12.4163 10.725C12.8163 10.325 12.8163 9.67502 12.4163 9.27502L6.98301 3.84168C6.74134 3.60002 6.74134 3.20002 6.98301 2.95835C7.22467 2.71668 7.62467 2.71668 7.86634 2.95835L13.2997 8.39168C13.7247 8.81668 13.9663 9.39168 13.9663 10C13.9663 10.6083 13.733 11.1833 13.2997 11.6083L7.86634 17.0417C7.74134 17.1583 7.58301 17.225 7.42467 17.225Z"
              fill="#F9F9F9"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};
